import { Deferred } from "@lib/utils/deferred"
import { TrackInfo } from "../types"
import { SharedMixerStateBuffers } from "./shared-state"
import {
  MainThreadToWorkerMessage,
  StreamingWorkerRegion,
  WorkerToMainThreadMessage,
} from "./worker/messages"

interface StreamingWorkerClientOptions {
  audioContext: AudioContext
  onError: (error: Error) => void
  onReady: () => void
  regions: StreamingWorkerRegion[]
  sharedStateBuffers: SharedMixerStateBuffers
  trackBufferSabs: SharedArrayBuffer[]
  tracks: Pick<TrackInfo, "id" | "order">[]
}

export class StreamingWorkerClient {
  private nextPlayRequestId = 1
  private readonly worker: Worker
  private readonly pendingPlayRequests = new Map<number, Deferred<void>>()
  private nextSeekRequestId = 1
  private readonly pendingSeekRequests = new Map<number, Deferred<void>>()

  constructor(options: StreamingWorkerClientOptions) {
    this.worker = new Worker(new URL("./worker/StreamingWorker.ts", import.meta.url), {
      type: "module",
    })

    this.worker.onmessage = (event: MessageEvent<WorkerToMainThreadMessage>) => {
      const message = event.data

      switch (message.type) {
        case "error": {
          const error = new Error(message.payload.message)
          this.rejectPendingPlayRequests(error)
          this.rejectPendingSeekRequests(error)
          options.onError(error)
          break
        }
        case "play-ready": {
          const deferred = this.pendingPlayRequests.get(message.payload.requestId)
          if (!deferred) {
            break
          }

          this.pendingPlayRequests.delete(message.payload.requestId)
          deferred.resolve()
          break
        }
        case "ready": {
          options.onReady()
          break
        }
        case "seek-complete": {
          const deferred = this.pendingSeekRequests.get(message.payload.requestId)
          if (!deferred) {
            break
          }

          this.pendingSeekRequests.delete(message.payload.requestId)
          deferred.resolve()
          break
        }
      }
    }

    this.worker.onerror = (event: ErrorEvent) => {
      const error = new Error(event.message || "Streaming worker crashed")
      this.rejectPendingPlayRequests(error)
      this.rejectPendingSeekRequests(error)
      options.onError(error)
    }

    this.postMessage({
      payload: {
        regions: options.regions,
        sampleRate: options.audioContext.sampleRate,
        sharedStateBuffers: options.sharedStateBuffers,
        trackBufferSabs: options.trackBufferSabs,
        tracks: options.tracks,
      },
      type: "init",
    })
  }

  destroy(): void {
    this.rejectPendingPlayRequests(
      new Error("Streaming worker destroyed before playback started")
    )
    for (const deferred of this.pendingSeekRequests.values()) {
      deferred.reject(new Error("Streaming worker destroyed before seek completed"))
    }
    this.pendingSeekRequests.clear()

    this.postMessage({ type: "destroy" })
    this.worker.terminate()
  }

  pause(): void {
    this.rejectPendingPlayRequests(
      new Error("Streaming playback paused before start completed")
    )
    this.postMessage({ type: "pause" })
  }

  play(timestamp: number, playbackRate: number): Promise<void> {
    const requestId = this.nextPlayRequestId++
    const deferred = new Deferred<void>()
    this.pendingPlayRequests.set(requestId, deferred)

    this.postMessage({
      payload: { playbackRate, requestId, timestamp },
      type: "play",
    })

    return deferred.promise
  }

  seek(timestamp: number, playbackRate: number): Promise<void> {
    this.rejectPendingPlayRequests(
      new Error("Streaming playback was superseded by a seek")
    )

    const requestId = this.nextSeekRequestId++
    const deferred = new Deferred<void>()
    this.pendingSeekRequests.set(requestId, deferred)

    this.postMessage({
      payload: { playbackRate, requestId, timestamp },
      type: "seek",
    })

    return deferred.promise
  }

  setPlaybackRate(rate: number): void {
    this.postMessage({ payload: { rate }, type: "setPlaybackRate" })
  }

  addRegion(region: StreamingWorkerRegion): void {
    this.postMessage({ payload: region, type: "addRegion" })
  }

  private rejectPendingPlayRequests(error: Error): void {
    for (const deferred of this.pendingPlayRequests.values()) {
      deferred.reject(error)
    }
    this.pendingPlayRequests.clear()
  }

  private rejectPendingSeekRequests(error: Error): void {
    for (const deferred of this.pendingSeekRequests.values()) {
      deferred.reject(error)
    }
    this.pendingSeekRequests.clear()
  }

  private postMessage(message: MainThreadToWorkerMessage): void {
    if (message.type === "init" || message.type === "addRegion") {
      const regions = message.type === "init" ? message.payload.regions : [message.payload]
      const transferables: ArrayBuffer[] = []
      for (const region of regions) {
        if (region.preDecodedPcm) {
          transferables.push(region.preDecodedPcm)
        } else {
          transferables.push(region.encodedAudioData)
        }
      }
      this.worker.postMessage(message, transferables)
      return
    }

    this.worker.postMessage(message)
  }
}
